import React, { useCallback, ComponentProps } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import { mediumRoundedCorners } from '../RoundedCorners';
import grey from '../Palettes/grey';
import overlay from '../Palettes/overlay';
import colors from '../Theme/colors';

interface ControllerRenderProperties {
  onChange: (...event: unknown[]) => void;
  value: { name?: string } | null | undefined;
  ref: React.Ref<HTMLInputElement>;
}

const FileUploadWrapper = styled.div`
  margin: 0 0 0 4px;
`;

export const FileUploadLabel = styled.label`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.sizes.h6};
  color: ${overlay[900]};
  margin: 0 0 6px 2px;
  display: block;
  opacity: 1;
  line-height: 1;
`;

export const FileUploadHelpText = styled.div`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.sizes.subtitle1};
  color: ${grey[650]};
  margin: 6px 0 6px 2px;
`;

const FileUploadButton = styled.div`
  border-style: solid;
  border-width: 2px;
  border-color: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.primary.main};
  text-transform: uppercase;
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.sizes.button};
  letter-spacing: 1px;
  font-weight: 500;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  min-width: 64px;
  height: 36px;
  overflow: hidden;
  vertical-align: middle;
  margin: 6px 0 0;
  cursor: pointer;
  ${mediumRoundedCorners};
`;

const FileUploadInput = styled.input`
  font-family: ${({ theme }) => theme.fonts.secondary};
  color: ${overlay[600]};
  display:block;
  margin-bottom: 4px;
  opacity: 0;
  height: 0;
  position: absolute;
`;

const FileUploadFileName = styled.div`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.sizes.body1};
  color: ${colors.black};
  margin: 0 0 0 2px;
`;

const preventDefaultAndStopPropagation = (
  event: React.SyntheticEvent,
) => {
  event.stopPropagation();
  event.preventDefault();
};

const fileUploadPropertyTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func,
  htmlId: PropTypes.string,
  label: PropTypes.string,
  helpText: PropTypes.string,
  filename: PropTypes.string,
  accept: PropTypes.string,
  ariaLabel: PropTypes.string,
};

type FileUploadProperties = InferProps<
  typeof fileUploadPropertyTypes
>;

const FileUpload = React.forwardRef<
  HTMLInputElement,
  FileUploadProperties
>(
  (
    {
      label,
      ariaLabel,
      helpText,
      htmlId: htmlIdProperty,
      className,
      onChange,
      filename: filenameProperty,
      accept,
    }: FileUploadProperties,
    reference: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const htmlId = htmlIdProperty ?? 'fileUpload';
    const filename = filenameProperty ?? 'No File Selected.';

    return (
      <FileUploadWrapper
        className={className ?? undefined}
        onDragOver={preventDefaultAndStopPropagation}
        onDragEnter={preventDefaultAndStopPropagation}
        onDragLeave={preventDefaultAndStopPropagation}
        onDrop={useCallback((
          event: React.DragEvent<HTMLDivElement>,
        ) => {
          preventDefaultAndStopPropagation(event);
          if (onChange) {
            onChange({
              target: { files: event.dataTransfer.files },
            });
          }
        }, [onChange])}
      >
        <FileUploadLabel>
          {label}
          <FileUploadHelpText>
            {helpText}
          </FileUploadHelpText>
          <FileUploadButton>
            Select a file
          </FileUploadButton>
          <FileUploadInput
            id={htmlId}
            type="file"
            accept={accept ?? undefined}
            onChange={onChange ?? undefined}
            ref={reference}
            aria-label={ariaLabel ?? undefined}
          />
        </FileUploadLabel>
        <FileUploadFileName>
          { filename }
        </FileUploadFileName>
      </FileUploadWrapper>
    );
  },
);

FileUpload.propTypes = fileUploadPropertyTypes;

const fileUploadWithControllerPropertyTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.shape({}).isRequired,
  rules: PropTypes.shape({}).isRequired,
  noFileSelectedLabel: PropTypes.string,
  className: PropTypes.string,
  htmlId: PropTypes.string,
  label: PropTypes.string,
  helpText: PropTypes.string,
  accept: PropTypes.string,
  ariaLabel: PropTypes.string,
};

type FileUploadWithControllerProperties = Omit<
  InferProps<typeof fileUploadWithControllerPropertyTypes>,
  'control' | 'rules'
> & {
  control: ComponentProps<typeof Controller>['control'];
  rules: ComponentProps<typeof Controller>['rules'];
};

export const FileUploadWithController = ({
  name,
  control,
  rules,
  noFileSelectedLabel: noFileSelectedLabelProperty,
  label,
  helpText,
  htmlId,
  accept,
  ariaLabel,
  className,
}: FileUploadWithControllerProperties) => {
  const noFileSelectedLabel = noFileSelectedLabelProperty
    ?? 'No File Selected.';
  const render = useCallback(
    ({
      onChange, value, ref,
    }: Pick<
      ControllerRenderProperties,
      'onChange' | 'ref' | 'value'
    >) => (
      <FileUpload
        onChange={
          ({ target: { files } }: {
            target: { files: FileList };
          }) => onChange(files[0])
        }
        filename={value?.name ?? noFileSelectedLabel}
        ref={ref}
        label={label}
        helpText={helpText}
        htmlId={htmlId}
        accept={accept}
        ariaLabel={ariaLabel}
        className={className}
      />
    ),
    [
      noFileSelectedLabel,
      label,
      helpText,
      htmlId,
      accept,
      ariaLabel,
      className,
    ],
  );

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={render}
    />
  );
};

FileUploadWithController.propTypes = fileUploadWithControllerPropertyTypes;

export default FileUpload;
